/**
 * <s-product-gallery> — one-image-at-a-time product gallery.
 *
 * Required markup:
 *   [data-gallery-track]          — horizontal scroll-snap track.
 *   [data-gallery-slide]          — one per media item, direct children of the track.
 *   [data-gallery-thumb]          — optional thumbnail tabs with data-index.
 *   [data-gallery-counter]        — optional visible "1 / 5" counter.
 *   [data-gallery-announcer]      — polite live region; data-template uses [index] and [count].
 * Buttons use commandfor="<this id>" with command="--prev", "--next" or "--go" (+ data-index).
 */
class SProductGallery extends HTMLElement {
  #ac;
  #index = 0;
  #scrollRaf = 0;
  #announceRaf = 0;
  #track;
  #slides = [];
  #thumbs = [];

  connectedCallback() {
    this.#ac = new AbortController();
    const opts = { signal: this.#ac.signal };

    this.#track = this.querySelector('[data-gallery-track]');
    this.#slides = [...this.querySelectorAll('[data-gallery-slide]')];
    this.#thumbs = [...this.querySelectorAll('[data-gallery-thumb]')];
    if (!this.#track || this.#slides.length === 0) return;

    this.addEventListener('command', this.#handleCommand, opts);
    this.#track.addEventListener('scroll', this.#handleScroll, { ...opts, passive: true });
    this.querySelector('[role="tablist"]')?.addEventListener('keydown', this.#handleThumbKeydown, opts);

    this.#update(0, false);
  }

  disconnectedCallback() {
    this.#ac?.abort();
    cancelAnimationFrame(this.#scrollRaf);
    cancelAnimationFrame(this.#announceRaf);
  }

  #handleCommand = (event) => {
    const count = this.#slides.length;
    if (event.command === '--prev') this.#go((this.#index - 1 + count) % count);
    else if (event.command === '--next') this.#go((this.#index + 1) % count);
    else if (event.command === '--go') this.#go(Number(event.source?.dataset.index ?? 0));
  };

  #handleThumbKeydown = (event) => {
    const count = this.#slides.length;
    const rtl = getComputedStyle(this).direction === 'rtl';
    const forward = rtl ? 'ArrowLeft' : 'ArrowRight';
    const back = rtl ? 'ArrowRight' : 'ArrowLeft';
    let next = null;
    if (event.key === forward) next = (this.#index + 1) % count;
    else if (event.key === back) next = (this.#index - 1 + count) % count;
    else if (event.key === 'Home') next = 0;
    else if (event.key === 'End') next = count - 1;
    if (next === null) return;
    event.preventDefault();
    this.#go(next);
    this.#thumbs[next]?.focus();
  };

  #handleScroll = () => {
    cancelAnimationFrame(this.#scrollRaf);
    this.#scrollRaf = requestAnimationFrame(() => {
      const width = this.#track.clientWidth || 1;
      const index = Math.round(Math.abs(this.#track.scrollLeft) / width);
      const clamped = Math.min(Math.max(index, 0), this.#slides.length - 1);
      if (clamped !== this.#index) this.#update(clamped, true);
    });
  };

  #go(index) {
    if (Number.isNaN(index) || !this.#slides[index]) return;
    const reduce = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    const slide = this.#slides[index];
    const rtl = getComputedStyle(this.#track).direction === 'rtl';
    const left = rtl ? -(this.#track.scrollWidth - slide.offsetLeft - slide.offsetWidth) : slide.offsetLeft;
    this.#track.scrollTo({ left, behavior: reduce ? 'auto' : 'smooth' });
    this.#update(index, true);
  }

  #update(index, announce) {
    this.#index = index;
    const count = this.#slides.length;

    this.#slides.forEach((slide, i) => {
      const active = i === index;
      slide.toggleAttribute('inert', !active);
      slide.setAttribute('aria-hidden', String(!active));
    });

    this.#thumbs.forEach((thumb, i) => {
      const active = i === index;
      thumb.setAttribute('aria-selected', String(active));
      thumb.tabIndex = active ? 0 : -1;
    });

    const counter = this.querySelector('[data-gallery-counter]');
    if (counter) counter.textContent = `${index + 1} / ${count}`;

    if (!announce) return;
    const announcer = this.querySelector('[data-gallery-announcer]');
    if (!announcer) return;
    const text = (announcer.dataset.template || '')
      .replace('[index]', String(index + 1))
      .replace('[count]', String(count));
    cancelAnimationFrame(this.#announceRaf);
    announcer.textContent = '';
    this.#announceRaf = requestAnimationFrame(() => {
      announcer.textContent = text;
    });
  }
}

if (!customElements.get('s-product-gallery')) {
  customElements.define('s-product-gallery', SProductGallery);
}
